import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api, { extractErrorMessage } from '../../services/api';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import { DISPOSITION_MAP } from '../../components/intelligence';
import { timeRangeToDays, type TimeRange } from '../../components/filters/TimeRangeFilter';
import type { Lead, Run, ToastMessage, PreviewData, PreviewLead } from './leadsTypes';
import type { AddLeadFormState } from './AddLeadForm';

const ITEMS_PER_PAGE = 10;

export function useLeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [runs, setRuns] = useState<Map<string, Run>>(new Map());
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<Lead>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRunIds, setFilterRunIds] = useState<string[]>([]);
  const [filterTemperatures, setFilterTemperatures] = useState<string[]>([]);
  const [filterDispositions, setFilterDispositions] = useState<string[]>([]);
  const [filterSources, setFilterSources] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('week');
  const [sortField, setSortField] = useState<'created_at' | 'name' | 'current_temperature' | 'current_disposition' | 'phone' | 'email' | 'source' | 'attempts' | 'last_contact_at'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState<AddLeadFormState>({ name: '', phone: '', email: '', run_id: '' });
  const [addLeadSubmitting, setAddLeadSubmitting] = useState(false);
  /** Selected leads for "Run Campaign" (multi-select); store full data so selection works across pages */
  const [selectedLeads, setSelectedLeads] = useState<Lead[]>([]);
  /** Lead pending delete confirmation */
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [deletingLead, setDeletingLead] = useState(false);
  /** Bulk delete selected leads confirmation */
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);
  /** Lead for "call from Leads" modal (pick agent → start call) */
  const [leadToCall, setLeadToCall] = useState<Lead | null>(null);
  /** Run campaign modal (pick agent → continue to campaigns with preselected leads) */
  const [showRunCampaignModal, setShowRunCampaignModal] = useState(false);

  useEffect(() => {
    loadLeads();
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRunIds, filterTemperatures, filterDispositions, filterSources, sortField, sortDirection, timeRange]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const loadRuns = async () => {
    try {
      const days = timeRangeToDays(timeRange);
      const params: Record<string, number> = { limit: 1000 };
      if (days !== null) params.days = days;
      else params.days = 10000; // "All" — widen the server-side cap
      const response = await api.get('/runs/', { params });
      const runsMap = new Map<string, Run>();
      response.data.forEach((run: Run) => runsMap.set(run.id, run));
      setRuns(runsMap);
    } catch (error) {
      console.error('Failed to load runs:', error);
    }
  };

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const formatDateDDMMYYYY = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  };

  const getRunName = (runId: string | null, created_at?: string, source?: string | null): string => {
    if (runId) {
      const run = runs.get(runId);
      return run ? run.name : 'Unknown Campaign';
    }
    if (source === 'manual') return created_at ? `Added Lead - ${formatDateTime(created_at)}` : 'Added Lead';
    return created_at ? `Leads Upload - ${formatDateTime(created_at)}` : 'Leads Upload';
  };

  const loadLeads = async (pageOverride?: number) => {
    try {
      setLoading(true);
      if (pageOverride !== undefined) setCurrentPage(pageOverride);
      const days = timeRangeToDays(timeRange);
      const params: Record<string, number> = {};
      if (days !== null) params.days = days;
      const allResponse = await api.get('/leads/', { params });
      let allLeads: Lead[] = [];
      if (allResponse.data.results) allLeads = allResponse.data.results;
      else if (Array.isArray(allResponse.data)) allLeads = allResponse.data;
      setLeads(allLeads);
    } catch (error: any) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Failed to load leads' });
      setLeads([]);
      setTotalLeads(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddLead = () => {
    setAddLeadForm({ name: '', phone: '', email: '', run_id: '' });
    setShowAddLeadModal(true);
  };

  const handleCloseAddLead = () => setShowAddLeadModal(false);

  const handleAddLeadSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setAddLeadSubmitting(true);
    try {
      const params: Record<string, string> = {};
      if (addLeadForm.run_id) params.run_id = addLeadForm.run_id;
      await api.post('/leads/', { name: addLeadForm.name.trim(), phone: addLeadForm.phone.trim(), email: addLeadForm.email.trim() || undefined }, { params });
      setToast({ type: 'success', message: 'Lead added successfully' });
      track(AnalyticsEvents.LEAD_ADD);
      setAddLeadForm({ name: '', phone: '', email: '', run_id: '' });
      setShowAddLeadModal(false);
      loadLeads(1);
    } catch (error: any) {
      setToast({ type: 'error', message: extractErrorMessage(error, 'Failed to add lead') });
    } finally {
      setAddLeadSubmitting(false);
    }
  };

  const handleEdit = (lead: Lead) => {
    setEditingId(lead.id);
    setEditData({ name: lead.name, phone: lead.phone, email: lead.email || '' });
  };

  const handleSave = async (leadId: string) => {
    try {
      await api.put(`/leads/${leadId}`, {
        name: editData.name,
        phone: editData.phone,
        email: editData.email || null,
      });
      setLeads(leads.map((lead) => (lead.id === leadId ? { ...lead, ...editData } : lead)));
      setEditingId(null);
      setEditData({});
      setToast({ type: 'success', message: 'Lead updated successfully' });
      track(AnalyticsEvents.LEAD_UPDATE);
    } catch (error: any) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Failed to update lead' });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const parseExcelPreview = (file: File): Promise<{ leads: PreviewLead[]; errors: string[] }> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          if (jsonData.length < 2) {
            resolve({ leads: [], errors: ['Excel file must contain at least a header row and one data row'] });
            return;
          }
          const headers = jsonData[0].map((h: any) => String(h || '').trim().toLowerCase());
          if (!headers.includes('name') || !headers.includes('phone')) {
            resolve({ leads: [], errors: ['Excel file must contain "name" and "phone" columns'] });
            return;
          }
          const parsedLeads: PreviewLead[] = [];
          const errors: string[] = [];
          for (let i = 1; i < jsonData.length; i++) {
            const rowNumber = i + 1;
            const row = jsonData[i];
            if (!row || row.length === 0) continue;
            const name = String(row[headers.indexOf('name')] || '').trim();
            const phone = String(row[headers.indexOf('phone')] || '').trim();
            const emailIndex = headers.indexOf('email');
            const email = emailIndex >= 0 && row[emailIndex] ? String(row[emailIndex]).trim() : undefined;
            if (!name || !phone) {
              errors.push(`Row ${rowNumber}: Name and phone are required`);
              continue;
            }
            parsedLeads.push({ name, phone, email });
          }
          resolve({ leads: parsedLeads, errors });
        } catch (error: any) {
          resolve({ leads: [], errors: [`Error parsing Excel file: ${error.message}`] });
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const parseCSVPreview = (file: File): Promise<{ leads: PreviewLead[]; errors: string[] }> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter((line) => line.trim());
        if (lines.length < 2) {
          resolve({ leads: [], errors: ['CSV must contain at least a header row and one data row'] });
          return;
        }
        const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
        if (!headers.includes('name') || !headers.includes('phone')) {
          resolve({ leads: [], errors: ['CSV must contain "name" and "phone" columns'] });
          return;
        }
        const parsedLeads: PreviewLead[] = [];
        const errors: string[] = [];
        for (let i = 1; i < lines.length; i++) {
          const rowNumber = i + 1;
          const values = lines[i].split(',').map((v) => v.trim());
          if (values.length < headers.length) {
            errors.push(`Row ${rowNumber}: Insufficient columns`);
            continue;
          }
          const name = values[headers.indexOf('name')] || '';
          const phone = values[headers.indexOf('phone')] || '';
          const email = headers.includes('email') ? values[headers.indexOf('email')] || '' : undefined;
          if (!name || !phone) {
            errors.push(`Row ${rowNumber}: Name and phone are required`);
            continue;
          }
          parsedLeads.push({ name, phone, email });
        }
        resolve({ leads: parsedLeads, errors });
      };
      reader.readAsText(file);
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel';
    if (!isCSV && !isExcel) {
      setToast({ type: 'error', message: 'Please select a valid CSV or Excel file (.csv, .xlsx, .xls)' });
      return;
    }
    const parseResult = isExcel ? await parseExcelPreview(file) : await parseCSVPreview(file);
    const { leads: parsedLeads, errors } = parseResult;
    if (errors.length > 0 && parsedLeads.length === 0) {
      setToast({ type: 'error', message: `File parsing errors: ${errors.join(', ')}` });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setPreviewData({ file, leads: parsedLeads, errors });
    setShowPreviewModal(true);
  };

  const handleConfirmUpload = async () => {
    if (!previewData) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', previewData.file);
      let url = '/leads/upload';
      if (filterRunIds.length === 1) url += `?run_id=${filterRunIds[0]}`;
      const response = await api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const created = response.data.created ?? 0;
      const errors: string[] = response.data.errors ?? [];
      let message = `Successfully uploaded ${created} lead${created !== 1 ? 's' : ''}`;
      if (errors.length > 0) message += `. ${errors.length} row${errors.length !== 1 ? 's' : ''} skipped due to duplication.`;
      setToast({ type: 'success', message });
      track(AnalyticsEvents.LEAD_IMPORT_CSV, { count: created });
      if (fileInputRef.current) fileInputRef.current.value = '';
      setShowPreviewModal(false);
      setPreviewData(null);
      loadLeads();
    } catch (error: any) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Failed to upload leads' });
    } finally {
      setUploading(false);
    }
  };

  const handleCancelPreview = () => {
    setShowPreviewModal(false);
    setPreviewData(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const selectedLeadIds = new Set(selectedLeads.map((l) => l.id));

  const toggleSelection = (lead: Lead) => {
    setSelectedLeads((prev) => {
      const exists = prev.some((l) => l.id === lead.id);
      if (exists) return prev.filter((l) => l.id !== lead.id);
      return [...prev, lead];
    });
  };

  const selectAllOnPage = () => {
    const visibleIds = new Set(paginatedLeads.map((l) => l.id));
    const allSelected = visibleIds.size > 0 && paginatedLeads.every((l) => selectedLeadIds.has(l.id));
    if (allSelected) {
      setSelectedLeads((prev) => prev.filter((l) => !visibleIds.has(l.id)));
    } else {
      setSelectedLeads((prev) => {
        const byId = new Map(prev.map((l) => [l.id, l]));
        paginatedLeads.forEach((l) => byId.set(l.id, l));
        return Array.from(byId.values());
      });
    }
  };

  const clearSelection = () => setSelectedLeads([]);

  const openBulkDeleteConfirm = () => {
    if (selectedLeads.length === 0) {
      setToast({ type: 'error', message: 'Select at least one lead to delete' });
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const closeBulkDeleteConfirm = () => {
    if (!deletingBulk) setShowBulkDeleteConfirm(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    const idsToDelete = selectedLeads.map((l) => l.id);
    try {
      setDeletingBulk(true);
      await Promise.all(idsToDelete.map((id) => api.delete(`/leads/${id}`)));
      setLeads(leads.filter((l) => !idsToDelete.includes(l.id)));
      setTotalLeads((prev) => Math.max(0, prev - idsToDelete.length));
      setSelectedLeads([]);
      setShowBulkDeleteConfirm(false);
      setDeletingBulk(false);
      track(AnalyticsEvents.LEAD_BULK_DELETE, { count: idsToDelete.length });
      requestAnimationFrame(() => {
        setToast({ type: 'success', message: `${idsToDelete.length} lead${idsToDelete.length !== 1 ? 's' : ''} deleted successfully` });
      });
    } catch (error: any) {
      setDeletingBulk(false);
      setToast({ type: 'error', message: error.response?.data?.detail || 'Failed to delete some leads' });
    }
  };

  const handleRunCampaign = () => {
    if (selectedLeads.length === 0) {
      setToast({ type: 'error', message: 'Select at least one lead to run a campaign' });
      return;
    }
    setShowRunCampaignModal(true);
  };

  const handleCloseRunCampaignModal = () => setShowRunCampaignModal(false);

  /** Open "call lead" modal: pick saved agent and start call from this page. */
  const handleRunCampaignForLead = (lead: Lead) => setLeadToCall(lead);
  const handleCloseCallModal = () => setLeadToCall(null);

  const openDeleteConfirm = (lead: Lead) => setLeadToDelete(lead);
  const closeDeleteConfirm = () => {
    if (!deletingLead) setLeadToDelete(null);
  };

  const handleConfirmDeleteLead = async () => {
    if (!leadToDelete) return;
    const leadId = leadToDelete.id;
    try {
      setDeletingLead(true);
      await api.delete(`/leads/${leadId}`);
      setLeads(leads.filter((l) => l.id !== leadId));
      setTotalLeads((prev) => Math.max(0, prev - 1));
      setSelectedLeads((prev) => prev.filter((l) => l.id !== leadId));
      setLeadToDelete(null);
      setDeletingLead(false);
      track(AnalyticsEvents.LEAD_DELETE);
      // Show toast after modal closes so it's visible (portal renders at document.body)
      requestAnimationFrame(() => {
        setToast({ type: 'success', message: 'Lead deleted successfully' });
      });
    } catch (error: any) {
      setDeletingLead(false);
      setToast({ type: 'error', message: error.response?.data?.detail || 'Failed to delete lead' });
    }
  };

  const TEMP_SORT_ORDER: Record<string, number> = { hot: 0, warm: 1, lukewarm: 2, cold: 3, dead: 4 };

  const requestSort = (
    field: 'created_at' | 'name' | 'current_temperature' | 'current_disposition' | 'phone' | 'email' | 'source' | 'attempts' | 'last_contact_at'
  ) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    if (field === 'created_at' || field === 'attempts' || field === 'last_contact_at') {
      setSortDirection('desc');
    } else {
      setSortDirection('asc');
    }
  };

  const filteredLeads = leads
    .filter((lead) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (
          !lead.name.toLowerCase().includes(term) &&
          !lead.phone.includes(searchTerm) &&
          !(lead.email && lead.email.toLowerCase().includes(term))
        ) return false;
      }
      if (filterRunIds.length > 0) {
        const runId = lead.run_id ?? '';
        if (!filterRunIds.includes(runId)) return false;
      }
      if (filterTemperatures.length > 0) {
        const temp = lead.current_temperature ?? '';
        if (!filterTemperatures.includes(temp)) return false;
      }
      if (filterDispositions.length > 0) {
        const disposition = lead.current_disposition ?? '';
        if (!filterDispositions.includes(disposition)) return false;
      }
      if (filterSources.length > 0) {
        const source = lead.source ?? 'legacy';
        if (!filterSources.includes(source)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let result = 0;
      if (sortField === 'current_temperature') {
        result = (TEMP_SORT_ORDER[a.current_temperature ?? ''] ?? 99) - (TEMP_SORT_ORDER[b.current_temperature ?? ''] ?? 99);
      } else if (sortField === 'attempts') {
        result = (a.total_attempts ?? a.retry_count ?? 0) - (b.total_attempts ?? b.retry_count ?? 0);
      } else if (sortField === 'created_at') {
        result = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortField === 'last_contact_at') {
        result = new Date(a.last_contact_at ?? 0).getTime() - new Date(b.last_contact_at ?? 0).getTime();
      } else if (sortField === 'source') {
        result = getRunName(a.run_id, a.created_at, a.source).localeCompare(getRunName(b.run_id, b.created_at, b.source));
      } else {
        const av = String(a[sortField] ?? '').toLowerCase();
        const bv = String(b[sortField] ?? '').toLowerCase();
        result = av.localeCompare(bv);
      }
      if (result === 0) {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      return sortDirection === 'asc' ? result : -result;
    });

  const computedTotalLeads = filteredLeads.length;
  const computedTotalPages = Math.max(1, Math.ceil(computedTotalLeads / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, computedTotalPages);
  const pageStart = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
  const paginatedLeads = filteredLeads.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  useEffect(() => {
    if (safeCurrentPage !== currentPage) setCurrentPage(safeCurrentPage);
  }, [safeCurrentPage, currentPage]);

  useEffect(() => {
    setTotalLeads(computedTotalLeads);
    setTotalPages(computedTotalPages);
  }, [computedTotalLeads, computedTotalPages]);

  const runOptions = Array.from(runs.values())
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((run) => ({ value: run.id, label: run.name }));

  const dispositionOptions = Array.from(
    new Set(
      leads
        .map((lead) => lead.current_disposition)
        .filter((disposition): disposition is string => Boolean(disposition))
    )
  )
    .sort((a, b) => {
      const aLabel = DISPOSITION_MAP[a]?.label ?? a;
      const bLabel = DISPOSITION_MAP[b]?.label ?? b;
      return aLabel.localeCompare(bLabel);
    })
    .map((disposition) => ({
      value: disposition,
      label: DISPOSITION_MAP[disposition]?.label ?? disposition.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    }));

  const sourceOptions = [
    { value: 'manual', label: 'Added Lead' },
    { value: 'upload', label: 'Leads Upload' },
    { value: 'legacy', label: 'Legacy' },
  ];

  const leadTemperatureOptions = [
    { value: 'hot', label: 'Hot' },
    { value: 'warm', label: 'Warm' },
    { value: 'lukewarm', label: 'Lukewarm' },
    { value: 'cold', label: 'Cold' },
    { value: 'dead', label: 'Dead' },
  ];

  const isAllOnPageSelected =
    paginatedLeads.length > 0 && paginatedLeads.every((l) => selectedLeadIds.has(l.id));

  return {
    leads: paginatedLeads,
    runs,
    loading,
    uploading,
    toast,
    setToast,
    showPreviewModal,
    previewData,
    fileInputRef,
    searchTerm,
    setSearchTerm,
    filterRunIds,
    setFilterRunIds,
    filterTemperatures,
    setFilterTemperatures,
    filterDispositions,
    setFilterDispositions,
    filterSources,
    setFilterSources,
    timeRange,
    setTimeRange,
    runOptions,
    dispositionOptions,
    sourceOptions,
    leadTemperatureOptions,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    requestSort,
    editingId,
    editData,
    setEditData,
    currentPage,
    setCurrentPage,
    totalPages,
    totalLeads,
    itemsPerPage: ITEMS_PER_PAGE,
    formatDateDDMMYYYY,
    getRunName,
    loadLeads,
    handleEdit,
    handleSave,
    handleCancel,
    handleFileSelect,
    handleUploadClick,
    handleConfirmUpload,
    handleCancelPreview,
    showAddLeadModal,
    addLeadForm,
    setAddLeadForm,
    addLeadSubmitting,
    handleOpenAddLead,
    handleCloseAddLead,
    handleAddLeadSubmit,
    selectedLeadIds,
    selectedLeads,
    isAllOnPageSelected,
    toggleSelection,
    selectAllOnPage,
    clearSelection,
    handleRunCampaign,
    handleRunCampaignForLead,
    leadToCall,
    handleCloseCallModal,
    showRunCampaignModal,
    handleCloseRunCampaignModal,
    leadToDelete,
    openDeleteConfirm,
    closeDeleteConfirm,
    handleConfirmDeleteLead,
    deletingLead,
    showBulkDeleteConfirm,
    openBulkDeleteConfirm,
    closeBulkDeleteConfirm,
    handleConfirmBulkDelete,
    deletingBulk,
  };
}
